import { Box, Stack, Typography } from "@mui/material";
import { intersection, isEmpty, take } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import { groupTagsByCategory } from "../../components/tagSelect/tags";
import type { ECommerceOfferWithNames } from "../../eCommerce/eCommerceTypes";
import { useEcommerceOffers } from "../../eCommerce/useECommerceOffers";
import { startOfPreviousMonth } from "../../misc/dateUtils";
import { useLimit } from "../../misc/useLimit";
import type { ListPublicPackagesResponseItem } from "../../publicPackages/publicPackagesApi";
import { usePublicPackages } from "../../publicPackages/usePublicPackages";
import { PackagesFilter } from "../filter/PackagesFilter";
import { LightboxDialog } from "../LightboxDialog";
import { packageCountries } from "../packageTagsInfo";
import type { PublicPackagesSearchCriteria } from "../usePublicPackagesSearch";
import { usePublicPackagesSearch } from "../usePublicPackagesSearch";
import { EcommerceOffersDialog } from "./EcommerceOffersDialog";
import { getOffersForPackages } from "./getOffersForPackages";
import { PackagesListingCard } from "./PackagesListingCard";

function filterPackages(
  packages: ListPublicPackagesResponseItem[],
  searchCriteria: PublicPackagesSearchCriteria,
  onlyParentPackages: boolean,
  onlyEcommercePackages: boolean,
  packagesWithOffers: ListPublicPackagesResponseItem[],
) {
  const { tags, name, regions } = searchCriteria;
  const tagsByCategory = groupTagsByCategory(tags);
  return packages
    .filter((pkg) => filterByName(pkg, name))
    .filter((pkg) => isEmpty(regions) || (pkg.regionId != null && regions.includes(pkg.regionId)))
    .filter((p) => tagsMatches(p, tagsByCategory))
    .filter((p) => !onlyParentPackages || !p.parentPackageId)
    .filter((p) => !onlyEcommercePackages || packagesWithOffers.some((pkg) => pkg.id.id === p.id.id));
}

function filterByName(pkg: ListPublicPackagesResponseItem, name: string | undefined) {
  {
    if (!name) {
      return true;
    }

    const query = name.toLocaleLowerCase();
    const countries = packageCountries(pkg);
    return (
      pkg.name?.toLocaleLowerCase().includes(query) ||
      pkg.id.userFriendlyId?.toString().toLocaleLowerCase().includes(query) ||
      pkg.packageName.toString().toLocaleLowerCase().includes(query) ||
      !!countries.find((country) => country.toLocaleLowerCase().includes(query)) ||
      false
    );
  }
}

function tagsMatches({ tags }: ListPublicPackagesResponseItem, tagsByCategory: Record<string, string[]>) {
  return Object.entries(tagsByCategory).every(([category, tagsGroup]) => {
    const tagsForCategory = tags.filter((t) => t.category === category).map((t) => t.value);
    return intersection(tagsGroup, tagsForCategory).length > 0;
  });
}

export function PackagesListing() {
  const { packages } = usePublicPackages();
  const { limit, loadMore } = useLimit();
  const { searchCriteria } = usePublicPackagesSearch();
  const { tags } = searchCriteria;
  const [onlyParentPackages, setOnlyParentPackages] = useState(isEmpty(tags));
  const [onlyEcommercePackages, setOnlyEcommercePackages] = useState(false);
  const { offers } = useEcommerceOffers({ from: startOfPreviousMonth().format("YYYY-MM") });
  const offersForPackages = getOffersForPackages(packages, offers);
  const packagesWithOffers = packages.filter((pkg) => offersForPackages[pkg.id.id]?.length > 0);

  const filteredPackages = filterPackages(
    packages,
    searchCriteria,
    onlyParentPackages,
    onlyEcommercePackages,
    packagesWithOffers,
  );
  const [lightboxImage, setLightboxImage] = useState<string>();
  const [selectedOffersData, setSelectedOffersData] = useState<{
    offers: ECommerceOfferWithNames[];
    packageId: string;
  } | null>(null);

  useEffect(() => {
    setOnlyParentPackages(isEmpty(tags));
  }, [tags]);

  const navigate = useNavigate();
  return (
    <Stack
      spacing={4}
      sx={{
        marginTop: "1.5em",
        height: "100%",
      }}
    >
      <Box
        sx={{
          animation: "fadeIn 0.4s ease-out",
          width: "100%",
          px: { xs: 2, sm: 3, md: 4 },
          pt: 3,
          pb: 8,
        }}
      >
        <PackagesFilter
          filteredPackages={filteredPackages.length}
          onlyParentPackages={onlyParentPackages}
          onlyEcommercePackages={onlyEcommercePackages}
          setOnlyParentPackages={setOnlyParentPackages}
          setOnlyEcommercePackages={setOnlyEcommercePackages}
        />

        <Box>
          <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
              }}
            >
              Znaleziono:{" "}
              <Box
                component="span"
                sx={{
                  fontWeight: "bold",
                  color: "text.primary",
                }}
              >
                {filteredPackages.length} pakiety
              </Box>
            </Typography>
          </Box>
          <InfiniteScroll
            dataLength={limit < filteredPackages.length ? limit : filteredPackages.length}
            hasMore={filteredPackages.length > limit}
            fetchData={loadMore}
            scrollableTarget={null}
          >
            <Box
              sx={{
                display: "grid",
                // Elastyczna siatka: karty mają min 320px szerokości i dopasowują się do ekranu
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: 3,
              }}
            >
              {take(filteredPackages, limit).map((pkg) => (
                <PackagesListingCard
                  key={pkg.id.id}
                  value={pkg}
                  offerCount={offersForPackages[pkg.id.id]?.length ?? 0}
                  onOffersSelected={() =>
                    setSelectedOffersData({ offers: offersForPackages[pkg.id.id] ?? null, packageId: pkg.id.id })
                  }
                  onCalculationSelected={() => {
                    navigate(`/calculations/new?fromPackage=${pkg.id.id}`);
                  }}
                  onOpenImage={setLightboxImage}
                />
              ))}
            </Box>
          </InfiniteScroll>
        </Box>
      </Box>
      {/* LIGHTBOX MAPY */}
      <LightboxDialog imageUrl={lightboxImage} onClose={() => setLightboxImage(undefined)} />
      {selectedOffersData && (
        <EcommerceOffersDialog
          offers={selectedOffersData.offers}
          onDetailsClick={() => {
            navigate(`/public/packages/${selectedOffersData.packageId}`);
          }}
          onClose={() => setSelectedOffersData(null)}
        />
      )}
    </Stack>
  );
}
